import type { PointJson } from 'contracts/basic';
import { Matrix4, Vector3 } from 'three';

// eslint-disable-next-line no-restricted-imports
import { SPLIT_CHAR } from '../../constants';
// eslint-disable-next-line no-restricted-imports
import type { ENTITY_TYPES } from '../../constants';

export type ActiveMetaData = {
  axis?: Vector3;
  center?: Vector3;
};

export const createActiveMetaData = (
  { axis, center }: { axis?: PointJson; center?: PointJson },
  transform: Matrix4,
): ActiveMetaData => {
  const out: ActiveMetaData = {};
  if (Array.isArray(axis)) {
    const rotMatrix: Matrix4 = new Matrix4().extractRotation(transform);
    out.axis = new Vector3(...axis).applyMatrix4(rotMatrix);
  }
  if (Array.isArray(center)) {
    out.center = new Vector3(...center).applyMatrix4(transform);
  }
  return out;
};

/**
 * Constructs the entity name with body, entity type and entity index encoded in the name so that these parameters can be decoded after doing intersection tests of these entites
 * @param hostBodyId - the hostBodyId or `undefined`
 * @param entityType - the entity type
 * @param index - Optional: the index of the entity (it is not required for STL geometry)
 */
export const constructEntityName = (
  ...args: [string, ENTITY_TYPES, ...(number | string)[]] //[ hostBodyId, entityType, (entity index or id)? ]
): string => args.join(SPLIT_CHAR);

const maxShiftable = 1 << 30;

/**
 * Rapidily copy a repeated pattern of data in `buf` sourced from range `inputStart` to `inputEnd`.
 * @param srcBuffer - The input buffer to copy and pattern in dstBuffer
 * @param dstBuffer - The destination buffer
 * @param position - the position in dstBuffer to start copying to
 * @param count - the number of copies to pattern srcBuffer
 * @remarks use indice positions and not byte positions
 */
export const fillRepeatedPattern = <
  T extends
    | Float32Array
    | Float64Array
    | Int8Array
    | Uint8Array
    | Int16Array
    | Uint16Array
    | Int32Array
    | Uint32Array,
>(
  srcBuffer: T | number[],
  dstBuffer: T,
  position = 0,
  count: number = Math.floor((dstBuffer.length - position) / srcBuffer.length),
): void => {
  let patternLength: number = srcBuffer.length;
  const startPosition = position;
  const endPosition: number = position + count * patternLength;
  if (count < 1) {
    return;
  }
  if (endPosition > dstBuffer.length) {
    throw new Error(
      `Destination buffer does not have enough space to copy/pattern input of ${patternLength} ${count} times from ${position}`,
    );
  }
  if (endPosition > maxShiftable) {
    throw new Error(`Destination is too large`); //Can't double patternLength below (we keep our buffers <= (65535 * 4) so this is okay. We are under by factor of 4096)
  }

  //First copy of srcBuffer into destination buffer
  dstBuffer.set(srcBuffer, position);
  //eslint-disable-next-line no-param-reassign
  position += patternLength;
  //Pattern the copy `count` times using `copyWithin()` for performance.
  //* Doubles the pattern each iteration to take best advantage of `copyWithin()`
  while (position < endPosition) {
    if (position + patternLength > endPosition) {
      patternLength = endPosition - position; //next patternLength is the remainder
    }
    dstBuffer.copyWithin(
      position,
      startPosition,
      startPosition + patternLength,
    ); //copies next chunk of pattern
    //eslint-disable-next-line no-param-reassign
    position += patternLength;
    patternLength <<= 1; //double pattern to copy
  }
};
